import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Layout, Spin } from "antd";
import TopicSelector from "../../components/Shared/TopicSelector";
import SubtopicSelector from "../../components/Shared/SubtopicSelector";
import QuizCard from "../../components/Shared/QuizCard";
import EmptyState from "../../components/Shared/EmptyState";
import QuizGradients from "../../components/Shared/QuizGradients";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearSelectedCertificationCli, setTopics, setQuizzes, setQuestion, setTimeRemaining, RootState } from "../../store";
import { FullScreenContext } from "../../App";
import { baseUrl } from "../../constants/api";
import showToastMessage from "../../utils/showToastMessage";

interface Quiz {
  quiz_id: string;
  quiz_name: string;
  number_of_questions: number;
  quiz_duration: string;
  createdAt: string;
  appeared_on?: string;
  availability?: boolean;
  reattempt: boolean;
  score: number | null;
  quiz_type?: "regular" | "certification";
  passing_percentage?: number;
  attempt_count?: number;
  is_passed?: boolean | null;
}

const QuizPage1 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { handle } = useContext(FullScreenContext);
  const { topics } = useSelector((state: RootState) => state.quiz);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState<string>("All");
  const [activeTab, setActiveTab] = useState<"regular" | "certification">("regular");
  const { quizzes } = useSelector((state: RootState) => state.quiz);
  const [loading, setLoading] = useState(false);
  const token = useSelector((state: RootState) => state.account.token);
  const profile = useSelector((state: RootState) => state.account.profile);

  useEffect(() => {
    if (profile?.role?.toUpperCase() === "ADMIN") {
      navigate("/", { replace: true });
    }
  }, [profile, navigate]);

  const fetchTopics = useCallback(async () => {
    const apiUrl = `${baseUrl}/api/topic/get-all-topics`;
    setLoading(true);
    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const topicData = await response.json();
        dispatch(setTopics(topicData.data));
        if (topicData.data.length > 0 && !selectedTopic) {
          setSelectedTopic(topicData.data[0].topic_name);
        }
      } else {
        const errorText = await response.text();
        showToastMessage(`Failed to fetch topics: ${errorText}`, "error");
      }
    } catch (error) {
      showToastMessage("An error occurred while fetching topics.", "error");
    } finally {
      setLoading(false);
    }
  }, [token, dispatch, selectedTopic]);

  const fetchQuizzes = useCallback(async (topicName: string, subtopicName: string) => {
    const apiUrl = `${baseUrl}/api/quiz/get-quizes?topic_name=${topicName}&subtopic_name=${subtopicName}`;
    setLoading(true);
    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const quizData = await response.json();
        if (quizData?.quizes) {
          dispatch(setQuizzes(quizData?.quizes));
        } else {
          dispatch(setQuizzes([]));
        }
      } else {
        const errorText = await response.text();
        showToastMessage(`Failed to fetch quizzes: ${errorText}`, "error");
      }
    } catch (error) {
      showToastMessage("An error occurred while fetching quizzes.", "error");
    } finally {
      setLoading(false);
    }
  }, [token, dispatch]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  useEffect(() => {
    if (selectedTopic) {
      fetchQuizzes(selectedTopic, selectedSubtopic);
    }
  }, [selectedTopic, selectedSubtopic, fetchQuizzes]);

  const handleTopicSelect = (topic: string | null) => {
    setSelectedTopic(topic);
    setSelectedSubtopic("All");
    dispatch(setQuizzes([]));
  };

  const handleSubtopicSelect = (subtopic: string | null) => {
    if (subtopic) setSelectedSubtopic(subtopic);
  };

  const fetchQuestions = async (quizId: string) => {
    const apiUrl = `${baseUrl}/api/quiz/get-questions/${quizId}`;
    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const questionData = await response.json();
        const selectedQuiz = (quizzes as Quiz[]).find((quiz) => quiz.quiz_id === quizId);
        dispatch(setQuestion({
          questions: questionData.questions,
          quiz_duration: selectedQuiz ? selectedQuiz.quiz_duration : undefined
        }));
        if (selectedQuiz) {
          dispatch(setTimeRemaining(parseInt(selectedQuiz.quiz_duration) * 60));
        }
      } else {
        const errorText = await response.text();
        showToastMessage(`Failed to fetch questions: ${errorText}`, "error");
      }
    } catch (error) {
      showToastMessage("An error occurred while fetching questions.", "error");
    }
  };

  const handleStartQuiz = (quizId: string) => {
    const selectedQuiz = (quizzes as Quiz[]).find((quiz) => quiz.quiz_id === quizId);
    if (selectedQuiz?.quiz_type === "certification") {
      dispatch(clearSelectedCertificationCli());
      navigate(`/quiz/${quizId}/select-cli`);
      return;
    }

    if (selectedQuiz) {
      dispatch(setTimeRemaining(parseInt(selectedQuiz.quiz_duration) * 60));
    }
    dispatch(clearSelectedCertificationCli());
    setTimeout(() => {
      navigate(`/quiz/${quizId}/start`);
      if (handle) {
        handle.enter();
      }
      fetchQuestions(quizId);
    }, 100);
  };

  const { newQuizzes, completedQuizzes, newCertificationQuizzes, completedCertificationQuizzes } = useMemo(() => {
    const qList = (quizzes as Quiz[]) || [];
    const isAttempted = (quiz: Quiz) => quiz.score !== null || Boolean(quiz.appeared_on);
    const isVisibleAsNew = (quiz: Quiz) =>
      typeof quiz.availability === "boolean"
        ? quiz.availability
        : !quiz.reattempt && !isAttempted(quiz);

    const regularQuizzes = qList.filter((quiz) => quiz.quiz_type !== "certification");
    const certificationQuizzes = qList.filter((quiz) => quiz.quiz_type === "certification");

    return {
      newQuizzes: regularQuizzes.filter(q => isVisibleAsNew(q) && !q.reattempt && !isAttempted(q)),
      completedQuizzes: regularQuizzes.filter(q => isAttempted(q) || q.reattempt),
      newCertificationQuizzes: certificationQuizzes.filter((q) => Boolean(q.availability)),
      completedCertificationQuizzes: certificationQuizzes.filter((q) => !q.availability && (q.attempt_count || 0) > 0),
    };
  }, [quizzes]);

  const currentSubtopics = useMemo(() => {
    const topic = topics.find((t: any) => t.topic_name === selectedTopic);
    return topic ? ["All", ...topic.subtopics] : ["All"];
  }, [topics, selectedTopic]);

  const getSubtopicColorIndex = useCallback((quiz: any): number => {
    // Get the subtopic name from the quiz's populated topic field
    const subtopicName = quiz?.topic?.topic?.subtopic_name;
    if (!subtopicName) return 0;

    // Find the index in currentSubtopics (which starts with "All")
    const hasAll = currentSubtopics[0] === "All";
    const idx = currentSubtopics.indexOf(subtopicName);
    if (idx === -1) return 0;

    // Adjust for the "All" entry at the beginning
    return hasAll ? Math.max(0, idx - 1) : idx;
  }, [currentSubtopics]);

  const renderQuizCard = (quiz: Quiz, isCompleted: boolean, index: number) => {
    const colorIndex = getSubtopicColorIndex(quiz);

    const actions = (
      <>
        {isCompleted && quiz.score !== null && (
          <div className="flex flex-col items-end">
            <span className="text-xl font-bold text-[#111]">Score : <span className="text-[#111]">{Math.round(quiz.score * 100) / 100}</span></span>
            {quiz.reattempt ? (
              <button className="flex items-center gap-1.5 bg-none border-none font-semibold cursor-pointer text-sm transition-all duration-200 hover:opacity-70 text-[#D97706]" onClick={() => handleStartQuiz(quiz.quiz_id)}>
                Redo Quiz <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
              </button>
            ) : (
              <button className="flex items-center gap-1.5 bg-none border-none font-semibold cursor-pointer text-sm transition-all duration-200 hover:opacity-70 text-[#374151]" onClick={() => navigate(`/quiz/${quiz.quiz_id}/review`)}>
                View Quiz <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </button>
            )}
          </div>
        )}
        {!isCompleted && (
          <button className={`bg-[#FFFFFF] text-[#111] px-8 py-2 rounded-full font-medium text-base border border-[#111] cursor-pointer transition-all duration-300 hover:bg-[#C7D2FE] ${index === 0 ? 'bg-[#C7D2FE]' : ''}`} onClick={() => handleStartQuiz(quiz.quiz_id)}>
            Start Quiz
          </button>
        )}
      </>
    );

    return (
      <QuizCard
        key={quiz.quiz_id}
        quiz={quiz}
        index={index}
        colorIndex={colorIndex}
        isCompleted={isCompleted}
        actions={actions}
      />
    );
  };



  return (
    <Layout className="m-0 w-full rounded-2xl flex flex-col flex-grow bg-[#FFFFFF] min-h-screen">
      <QuizGradients />

      <div className="mx-auto w-full px-5">
        <TopicSelector
          topics={topics}
          selectedTopic={selectedTopic}
          handleTopicSelect={handleTopicSelect}
        />

        <div className='h-[10px]' />
        <div className="flex gap-3 mb-4">
          <button
            type="button"
            className={`px-5 py-2 rounded-full border text-sm font-medium transition-all ${activeTab === "regular" ? "bg-[#C7D2FE] border-[#111] text-[#111]" : "bg-white border-[#D1D5DB] text-[#4B5563]"}`}
            onClick={() => setActiveTab("regular")}
          >
            Learning Quizzes
          </button>
          <button
            type="button"
            className={`px-5 py-2 rounded-full border text-sm font-medium transition-all ${activeTab === "certification" ? "bg-[#C7D2FE] border-[#111] text-[#111]" : "bg-white border-[#D1D5DB] text-[#4B5563]"}`}
            onClick={() => setActiveTab("certification")}
          >
            Certificate Test
          </button>
        </div>
        <SubtopicSelector
          subtopics={currentSubtopics}
          selectedSubtopic={selectedSubtopic}
          handleSubtopicSelect={handleSubtopicSelect}
        />

        {loading ? (
          <div className="flex justify-center items-center p-[100px]">
            <Spin size="large" />
          </div>
        ) : (activeTab === "regular" ? (newQuizzes.length === 0 && completedQuizzes.length === 0) : (newCertificationQuizzes.length === 0 && completedCertificationQuizzes.length === 0)) ? (
          <EmptyState
            title={activeTab === "regular" ? "No Quizzes Available" : "No Certification Quizzes Available"}
            message={activeTab === "regular" ? "Please check back later or contact the admin for updates." : "No certification quizzes are available for the selected topic right now."}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {activeTab === "regular" && newQuizzes.length > 0 && (
              <>
                <div className="text-sm font-bold text-[#111] uppercase flex items-center gap-2 tracking-wider after:content-[''] after:flex-grow after:h-[1px] after:bg-[#E2E8F0]"><span className="shine-effect">NEW</span></div>
                {newQuizzes.map((q, i) => renderQuizCard(q, false, i))}
              </>
            )}

            {activeTab === "regular" && completedQuizzes.length > 0 && (
              <>
                <div className="text-sm font-bold text-[#111] uppercase flex items-center gap-2 tracking-wider after:content-[''] after:flex-grow after:h-[1px] after:bg-[#E2E8F0]">COMPLETED</div>
                {completedQuizzes.map((q, i) => renderQuizCard(q, true, i))}
              </>
            )}

            {activeTab === "certification" && newCertificationQuizzes.length > 0 && (
              <>
                <div className="text-sm font-bold text-[#111] uppercase flex items-center gap-2 tracking-wider after:content-[''] after:flex-grow after:h-[1px] after:bg-[#E2E8F0]"><span className="shine-effect">AVAILABLE</span></div>
                {newCertificationQuizzes.map((q, i) => renderQuizCard(q, false, i))}
              </>
            )}

            {activeTab === "certification" && completedCertificationQuizzes.length > 0 && (
              <>
                <div className="text-sm font-bold text-[#111] uppercase flex items-center gap-2 tracking-wider after:content-[''] after:flex-grow after:h-[1px] after:bg-[#E2E8F0]">ATTEMPTS</div>
                {completedCertificationQuizzes.map((quiz, index) => {
                  const certificationAction = quiz.availability ? (
                    <button className="flex items-center gap-1.5 bg-none border-none font-semibold cursor-pointer text-sm transition-all duration-200 hover:opacity-70 text-[#D97706]" onClick={() => handleStartQuiz(quiz.quiz_id)}>
                      Retry Certification
                    </button>
                  ) : (
                    <button className="flex items-center gap-1.5 bg-none border-none font-semibold cursor-pointer text-sm transition-all duration-200 hover:opacity-70 text-[#374151]" onClick={() => navigate(`/quiz/${quiz.quiz_id}/review`)}>
                      View Quiz
                    </button>
                  );

                  return (
                    <QuizCard
                      key={quiz.quiz_id}
                      quiz={quiz}
                      index={index}
                      colorIndex={getSubtopicColorIndex(quiz)}
                      isCompleted
                      actions={certificationAction}
                    />
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default QuizPage1;
